import { Pipe, PipeTransform, inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { LanguageService } from '../../core/services/language.service';
import { AiTranslationService } from '../../core/services/ai-translation.service';
import { Subscription } from 'rxjs';

@Pipe({
    name: 'translate',
    standalone: true,
    pure: false // Necessary to react to language changes and async updates
})
export class TranslatePipe implements PipeTransform, OnDestroy {
    private langService = inject(LanguageService);
    private aiService = inject(AiTranslationService);
    private cdr = inject(ChangeDetectorRef);

    private lastKey: string | null = null;
    private lastLang: string | null = null;
    private lastValue: string | null = null;
    private sub: Subscription | null = null;

    transform(key: string): string {
        if (!key) return '';

        const currentLang = this.langService.currentLang();

        // 1. If key and lang haven't changed, return cached value
        if (key === this.lastKey && currentLang === this.lastLang) {
            return this.lastValue || key;
        }

        // Reset if key/lang changed
        this.lastKey = key;
        this.lastLang = currentLang;
        
        if (this.sub) {
            this.sub.unsubscribe();
            this.sub = null;
        }

        // 2. Try static translation first
        const staticText = this.langService.getStaticTranslation(key);
        if (staticText) {
            this.lastValue = staticText;
            return staticText;
        }

        // 3. Fallback to AI translation (async)
        // If current language is Spanish (source), return key
        if (currentLang === 'es') {
            this.lastValue = key;
            return key;
        }

        // Placeholder while loading if we don't have a value yet
        if (!this.lastValue || this.lastValue === key) {
            // Optional: return key while loading or show a loading indicator
        }

        this.sub = this.aiService.translate(key, currentLang).subscribe(translated => {
            if (translated && translated !== this.lastValue) {
                this.lastValue = translated;
                this.cdr.markForCheck(); // Trigger UI refresh
            }
        });

        return this.lastValue || key;
    }

    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }
}
